<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';

// Define props
const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  unitConfig: {
    type: Object,
    required: true
  },
  lastValueArr: {
    type: Array,
    default: () => []
  },
  isSummary: {
    type: Boolean,
    default: false
  },
  inquiryTime: {
    type: Array,
    default: () => []
  },
  chartRef: {
    type: Object,
    default: null
  },
  disableHover: {
    type: Boolean,
    default: false
  },
  showTooltipOfChart: {
    type: Boolean,
    default: true
  },
  isNow: {
    type: Boolean,
    default: false
  }
});

const isMoveInLastValue = ref(false);
const isMoveInTooltip = ref(false);
const positionPopupTooltip = ref([]);
const keyChart = ref(0);
const saveTime = ref(moment());
// const hTooltip = ref(0);
// Use Composable
const { formatDateTime } = useDateTimeFormat();

// Define Computed
const isOneValue = computed(() => props.lastValueArr.length <= 1);
const lastIndex = computed(() => props.lastValueArr[0]?.lastIndex || 0);

const lastValue = computed(() => {
  if (!props.lastValueArr.length) return '';
  const sortedValues = groupLastValueByTime.value;
  const presentedEl = sortedValues[Object.keys(sortedValues)[0]];
  return presentedEl[0].lastValue;
});
const highestUnit = computed(() => props.unitConfig.highestUnit);
const unit = computed(() => props.unitConfig.unit);
const isConvertible = computed(() => props.unitConfig.isConvertible);

const displayValueInTooltip = (rawValue = 0) => {
  const value = Number(parseFloat(rawValue).toFixed(2));
  if (isConvertible.value && highestUnit.value === unit.value) return value;
  const val = value ? [value, highestUnit.value] : [value];
  if (isConvertible.value) return val.join(' ');
  return val.join('');
};

const oneLastValue = computed(() => {
  const roundedValue = Number(parseFloat(lastValue.value).toFixed(2));
  if (isConvertible.value) {
    return `${roundedValue} ${highestUnit.value}`;
  }
  let valString = `${roundedValue}`;
  if (highestUnit.value && roundedValue) {
    valString += `${highestUnit.value}`;
  }
  return `${valString} ${unit.value}`;
});

const multipleLastValue = computed(() => {
  const roundedValue = Number(parseFloat(lastValue.value).toFixed(2));
  if (isConvertible.value) {
    return `${roundedValue},... ${highestUnit.value}`;
  }
  let valString = `${roundedValue}`;
  if (highestUnit.value && roundedValue) {
    valString += `${highestUnit.value}`;
  }
  return `${valString},... ${unit.value}`;
});

const groupLastValueByTime = computed(() => {
  const groupedValues = {};
  const sortedValues = [...props.lastValueArr];
  sortedValues.sort((val1, val2) => val2.lastDate - val1.lastDate);
  for (const el of sortedValues) {
    const { lastDate } = el;
    const dateTime = new Date(lastDate);
    dateTime.setSeconds(0, 0);
    const unixValue = dateTime.getTime();
    if (groupedValues[unixValue]) groupedValues[unixValue].push(el);
    else groupedValues[unixValue] = [el];
  }
  for (const el in groupedValues) {
    groupedValues[el].sort((val1, val2) => val2.lastValue - val1.lastValue);
  }
  return groupedValues;
});

//Define functions
const getAnchorStyle = () => {
  if (!props.isSummary) return;
  nextTick(() => {
    const tooltipWrapper = document.getElementById(props.id);
    if (tooltipWrapper) {
      const tooltipWrapperBottom = tooltipWrapper.getBoundingClientRect().bottom;
      const elementParentBottom = document.getElementsByClassName('formbox-col')[0];
      if (elementParentBottom) {
        const bottomOfParentPosition = elementParentBottom.getBoundingClientRect().bottom;
        const setTopPosition = bottomOfParentPosition - tooltipWrapperBottom;
        if (setTopPosition < 0) {
          tooltipWrapper.style.top = `${setTopPosition}px`;
        }
      }
    }
  });
};

const onHover = () => {
  if (!props.chartRef || props.disableHover) return;
  props.chartRef.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: lastIndex.value
  });
};
const onLeave = () => {
  if (!props.chartRef || props.disableHover) return;
  props.chartRef.dispatchAction({
    type: 'hideTip'
  });
};
const showTooltipLastValue = (event) => {
  if (props.disableHover) return;
  if (!props.showTooltipOfChart) return;
  isMoveInLastValue.value = true;
  nextTick(() => {
    const htmlTooltips = document.getElementsByClassName('tooltip' + keyChart.value);
    const htmltitle = document.getElementsByClassName('title' + keyChart.value)[0] || '';
    if (!htmlTooltips || !htmlTooltips.length) return;

    let htmlTooltip = htmlTooltips[0];
    let posY = 0;
    let posX = 0;

    if (htmltitle) {
      const positionTitle = htmltitle.getBoundingClientRect();
      posY = positionTitle.top + positionTitle.height;
      posX = positionTitle.left;
    }

    if (htmlTooltip) {
      htmlTooltip;

      htmlTooltip.style.visibility = 'visible';

      // top -> right -> bottom -> left
      positionPopupTooltip.value = [
        event.clientY,
        event.clientX + htmlTooltip.clientWidth,
        event.clientY + htmlTooltip.clientHeight,
        event.clientX
      ];
    }

    let htmlModal = document.getElementsByClassName('lego-modal__container')[0] || '';

    if (htmlModal) {
      const wh = window.innerHeight;
      const ww = window.innerWidth;
      // -40 for beaty
      if (event.clientX + htmlTooltip.clientWidth + 40 > ww) {
        posX =
          (ww - htmlModal.clientWidth) / 2 + htmlModal.clientWidth - htmlTooltip.clientWidth - 40;
      }

      // -10 for beaty
      if (event.clientY + htmlTooltip.clientHeight > wh) {
        posY =
          (wh - htmlModal.clientHeight) / 2 +
          htmlModal.clientHeight -
          htmlTooltip.clientHeight -
          10;
      }
    }

    if (htmlTooltip) {
      htmlTooltip.style.transform = `translate3d(` + posX + `px, ` + posY + `px, 0px)`;
    }
  });
};
const onMoveInTooltip = () => {
  isMoveInTooltip.value = true;
};

const onMoveOutLastValue = (event) => {
  isMoveInLastValue.value = false;
  isMoveInTooltip.value = false;

  const [top, right, bottom, left] = positionPopupTooltip.value;

  if (
    left < event.clientX &&
    event.clientX < right &&
    top < event.clientY &&
    event.clientY < bottom &&
    props.lastValueArr.length > 12
  ) {
    isMoveInTooltip.value = true;
  }
};
const onMoveOutTooltip = () => {
  isMoveInTooltip.value = false;
};
const showTooltip = computed(() => {
  return isMoveInLastValue.value || isMoveInTooltip.value;
});
onMounted(() => {
  keyChart.value = new Date().getTime() + Math.random();
  getAnchorStyle();
});

watch(
  () => props.inquiryTime[1],
  (oldVal, newVal) => {
    if (oldVal !== newVal) saveTime.value = moment();
  },
  { deep: true }
);
</script>
<template>
  <div class="wrapper-last-value">
    <div v-if="isNow" class="show-latest-value">
      <div
        v-if="isOneValue"
        class="chart-last-value border-left last-value"
        @mouseenter="onHover"
        @mouseleave="onLeave"
      >
        <span>
          {{ oneLastValue }}
          <span class="lbl-last-value" v-if="!isSummary">({{ $t('label.lastValue') }})</span>
        </span>
      </div>
      <div v-else class="tooltip-wrapper">
        <div class="tooltip-icon border-left" v-if="lastValueArr && lastValueArr.length">
          <span
            class="last-value"
            :class="'title' + keyChart"
            @mouseenter="showTooltipLastValue"
            @mouseleave="onMoveOutLastValue"
          >
            {{ multipleLastValue }}
            <span class="lbl-last-value" v-if="!isSummary">({{ $t('label.lastValue') }})</span>
          </span>
          <teleport to="body">
            <div
              ref="tooltipMaxValue"
              class="tooltip-container toolbar-scrollbar tooltip-last-value"
              :class="'tooltip' + keyChart"
              v-if="showTooltip"
              @mouseenter="onMoveInTooltip"
              @mouseleave="onMoveOutTooltip"
            >
              <div v-for="(els, timeValue) in groupLastValueByTime" :key="timeValue">
                <div>{{ formatDateTime(Number(timeValue)) }}</div>
                <div v-for="el in els" :key="el.name" class="tooltip-wrapper">
                  <div class="ellipsis">
                    <span
                      class="tooltip-wrapper__marker"
                      :style="{ 'background-color': el.color }"
                    />
                    {{ el.name }}
                  </div>
                  <div class="tooltip-wrapper__value">
                    {{ displayValueInTooltip(el.lastValue) }}
                  </div>
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>
    </div>
    <div v-else class="no-available border-left last-value">
      <span>N/A</span>
      <span class="lbl-last-value" v-if="!isSummary">({{ $t('label.lastValue') }})</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper-last-value {
  display: flex;
  align-items: center;
  gap: 4px;
}
.tooltip-last-value {
  position: absolute;
  border-style: solid;
  white-space: nowrap;
  z-index: 99999999;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 2px 10px;
  // transition: opacity 0.1s cubic-bezier(0.23, 1, 0.32, 1),
  //   visibility 0.1s cubic-bezier(0.23, 1, 0.32, 1), transform 0.2s cubic-bezier(0.23, 1, 0.32, 1);
  background-color: rgb(255, 255, 255);
  border-width: 1px;
  border-radius: 4px;
  color: rgb(0, 0, 0);
  font: 12px / 18px 'Microsoft YaHei';
  top: 0px;
  left: 0px;
  transform: translate3d(4px, 237px, 0px);
  border-color: rgb(255, 255, 255);
  max-height: 300px;
  overflow: auto;
  max-width: 600px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  visibility: hidden;

  .tooltip-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: 16px;
    margin-top: 4px;
    &__marker {
      display: inline-block;
      margin-right: 4px;
      border-radius: 10px;
      width: 10px;
      height: 10px;
    }

    &__value {
      padding-left: 8px;
      font-weight: bold;
    }
  }

  .content-wrapper {
    padding: 8px;
  }

  .tooltip-date {
    width: max-content;
  }

  .tooltip-content {
    border-spacing: 0px;
    width: 100%;

    tr {
      td {
        padding-top: 4px;
        padding-right: 4px;
      }
    }

    &_info {
      display: flex;
      align-items: center;
    }

    &_color {
      height: 10px;
      min-width: 10px;
      border-radius: 100%;
      margin-right: 4px;
    }

    &_name {
      max-width: 370px;
    }
    &_max_value {
      width: max-content;
      padding-right: 12px;
    }

    &_value {
      font-weight: 700;
      text-align: right;
      padding-right: 0px !important;
    }
  }
}
.chart-last-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 16px;
  font-weight: 500;
  color: #2f3237;
  .last-value-text {
    font-weight: 400;
  }
}
.border-left {
  position: relative;
  &::before {
    content: '';
    display: block;
    position: absolute;
    left: -16px;
    top: 50%;
    width: 1px;
    height: 16px;
    background-color: #b3b7c1;
    transform: translate(0, -50%);
  }
}
.tooltip-wrapper {
  line-height: 16px;
  .tooltip-icon {
    &:hover {
      .tooltip-container {
        visibility: visible;
      }
    }
  }
}
.last-value {
  font-weight: 500;
  font-size: 16px;
  white-space: nowrap;
}
.lbl-last-value {
  font-size: 13px;
  font-weight: 400;
  color: #555b68;
  line-height: normal;
  margin-top: 2px;
  margin-left: 4px;
}
</style>
